<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onWifiResponse } from '../NetworkComponents/network_helpers';
	import { isWallDisplay, real_id } from '../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
	import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Checkbox, Button, Alert, Icon, Text } from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import ScanAvailableNetworks from './WiFiScanAvailableNetworksG2.svelte';
	import StaticIPAddressForm from '../NetworkComponents/StaticIPAddressForm.svelte';
	import SSIDForm from '../NetworkComponents/SSIDForm.svelte';
	import WifiClientStatus from '../NetworkComponents/WifiClientStatus.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import ModalPrompt from 'src/components/ModalPrompt.svelte';
	import { getContext, onDestroy, onMount } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = readDeviceSettings(id);

	$: statusStore = getDeviceStatus(id);

	let ssid = settings?.wifi.sta.ssid,
		pass = settings?.wifi.sta.pass,
		static_ip = settings?.wifi.sta.ipv4mode === 'static',
		is_open = settings?.wifi.sta.ssid !== null ? settings?.wifi.sta.is_open : false,
		enable = settings?.wifi.sta.enable,
		waitForResponse = false,
		ssidForm: SSIDForm,
		staticIpAddressForm: StaticIPAddressForm,
		modal: NetworkModal,
		postReqData: {
			enable: number;
			ipv4mode?: string;
			ssid?: string;
			pass?: string;
			is_open?: boolean;
			ip?: string;
			netmask?: string;
			gw?: string;
			nameserver?: string;
		},
		openAlert = false;

	/**
	 * Wall-display Wi-Fi can be changed from the device's touchscreen. The
	 * firmware doesn't push an updated `Wifi.GetConfig` for that, so
	 * `$settingsStore.wifi.sta.ssid` stays stale — but the live status
	 * (`status.wifi.ssid` from readDeviceStatus) does reflect the network the device
	 * is now connected to. Reflect that change in the form so the SSID input
	 * auto-fills and `WifiClientStatus` flips to "Device is connected".
	 */
	let formBaseline = { ssid, pass: pass ?? '' };

	const isFormPristine = () => ssid === formBaseline.ssid && (pass ?? '') === formBaseline.pass;

	$: if (isWallDisplay(device?.type)) {
		const liveSsid = $statusStore?.wifi?.ssid;
		if (liveSsid && liveSsid !== formBaseline.ssid && isFormPristine()) {
			ssid = liveSsid;
			pass = '';
			formBaseline = { ssid: liveSsid, pass: '' };
			ssidForm?.reset?.();
		}
	}

	/**
	 * On the deployed app the cloud-relayed `Shelly:Status` events can lag
	 * behind a touchscreen-initiated Wi-Fi switch (the device drops its old
	 * cloud session and the cached status is pre-switch). Locally this isn't
	 * a problem because RPC and status pushes go directly over LAN. To make
	 * the cloud path behave the same, proactively pull a fresh status when
	 * the panel mounts and merge it into `devices_status` — the reactive
	 * sync above will then pick up the new SSID.
	 */
	onMount(async () => {
		if (!isWallDisplay(device?.type) || isOffline) return;
		try {
			const res: SettingRequest = await gen2Request(device, 'Shelly.GetStatus', {});
			const fresh = res?.isok && (res as any)?.data;
			if (fresh) {
				const current = devices_status.getByRealId(id) || {};
				devices_status.update_device_status({
					id,
					msg: {
						...current,
						...fresh,
						wifi: { ...(current.wifi ?? {}), ...(fresh.wifi ?? {}) },
					},
				});
			}
		} catch {
			/* fall back to whatever's already in the store */
		}
	});

	$: staticSettings = {
		ip:
			enable && !static_ip && $statusStore?.wifi?.ssid == ssid
				? $statusStore?.wifi?.sta_ip
				: settings?.wifi.sta.ip,
		mask: settings?.wifi.sta.netmask,
		gw: settings?.wifi.sta.gw,
		dns: settings?.wifi.sta.nameserver,
	};

	const connect = async () => {
		waitForResponse = true;
		const params = {
				config: { sta: postReqData },
			},
			res: SettingRequest = await gen2Request(device, 'Wifi.SetConfig', params);
		if (res.isok) {
			settings.wifi.sta = { ...settings?.wifi.sta, ...postReqData };
			devices_settings.update_devices_settings(id, settings);
			pass = '';
			formBaseline = { ssid, pass: '' };
		}
		waitForResponse = false;
		onWifiResponse(res, ssid, enable, device);
	};

	const validate = () => {
		postReqData = { enable };
		if (enable) {
			if (
				ssid !== '' &&
				(ssid != settings?.wifi.sta.ssid || pass != '' || is_open != settings?.wifi.sta.is_open)
			) {
				const ssidParams = ssidForm.validate();
				if (!ssidParams) return false;
				postReqData.ssid = ssidParams.ssid;
				postReqData.is_open = ssidParams.is_open;
				postReqData.pass = ssidParams.pass;
			}
			if (static_ip) {
				const staticIpAddress = staticIpAddressForm.validate();
				if (!staticIpAddress) return false;
				postReqData.ipv4mode = 'static';
				postReqData.ip = staticIpAddress.ip;
				postReqData.netmask = staticIpAddress.mask;
				postReqData.gw = staticIpAddress.gw;
				postReqData.nameserver = staticIpAddress.dns;
			} else {
				postReqData.ipv4mode = 'dhcp';
			}
		}
		if (ssid === '') {
			openAlert = true;
		} else {
			modal.open();
		}
	};

	const clearSSIDData = () => {
		ssid = null;
		enable = false;
		pass = null;
		postReqData = { ssid, pass, enable };
		connect();
	};

	$: hasAnotherNetworkSetUp = settings?.wifi?.sta1?.enable;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Checkbox size="l" class="mb-xs" bind:checked={enable} disabled={isOffline} id="toggle-76dspbz">
		{capFirstChar($format('sub_settings_title_wifi_mode_client', { default: 'wi-fi 1' }))}
	</Checkbox>

	<WifiClientStatus {isOffline} {id} ssid={formBaseline.ssid} classes="mb-s-plus" />

	<ScanAvailableNetworks
		{isOffline}
		{device}
		{enable}
		on:select={(e) => {
			ssidForm.reset();
			ssid = e.detail['value'];
			is_open = e.detail['is_open'];
		}}
	/>

	<SSIDForm
		{isOffline}
		disableForm={!enable || isOffline}
		bind:this={ssidForm}
		bind:ssid
		bind:pass
		bind:is_open
		removeBottomPadding={isWallDisplay(device.type)}
		removePaddings
	/>

	{#if !isWallDisplay(device.type)}
		<hr class="mt-s mb-m" />
		<StaticIPAddressForm
			removeBottomMargin
			removePaddings
			disabledForm={!enable || isOffline}
			settings={staticSettings}
			bind:this={staticIpAddressForm}
			bind:open={static_ip}
		/>
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enable} disabled={isOffline} id="checkbox-76dspbz">
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	<hr class="mb-m mt-s" />

	<WifiClientStatus {isOffline} {id} ssid={formBaseline.ssid} />

	<ScanAvailableNetworks
		{isOffline}
		{device}
		{enable}
		on:select={(e) => {
			ssidForm.reset();
			ssid = e.detail['value'];
			is_open = e.detail['is_open'];
		}}
	/>

	<SSIDForm
		{isOffline}
		disableForm={!enable || isOffline}
		bind:this={ssidForm}
		bind:ssid
		bind:pass
		bind:is_open
	/>

	{#if !isWallDisplay(device.type)}
		<StaticIPAddressForm
			disabledForm={!enable || isOffline}
			settings={staticSettings}
			bind:this={staticIpAddressForm}
			bind:open={static_ip}
		/>
	{/if}

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	title={enable
		? $format('save_wifi_settings_title', {
				default: 'Save Wi-Fi {type} Settings?',
				values: { type: '1' },
			})
		: $format('disable_wifi_settings_title', {
				default: 'Disable Wi-Fi {type}?',
				values: { type: '1' },
			})}
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: enable ? $format('button_save') : $format('disable'),
	}}
	hideSlotAdditionalInfo={!enable}
>
	{#if enable}
		{$format('wifi_prompt_modal_enable', {
			values: { ssid },
			default: 'Your device will connect to ‘{ssid}’ when the signal is strong enough.',
		})}
	{:else if hasAnotherNetworkSetUp}
		{$format('wifi_prompt_modal_disabled_auto_connect', {
			values: { value: 'Wi-Fi 1', secondValue: 'Wi-Fi 2' },
			default:
				'By disabling ‘{value}’ your device will automatically connect to the configured ‘{secondValue}’ network to maintain connectivity.',
		})}
	{:else}
		{$format('wifi_prompt_modal_disabled', {
			values: { value: 'Wi-Fi 1' },
			default: 'By disabling ‘{value}’ you may lose connection to the device.',
		})}
	{/if}

	<div class="mt-m" slot="additionalInfo">
		<Alert
			type="warning"
			heading={$format('require_device_reboot_prompt', {
				default: 'Note that this action might require a device reboot.',
			})}
		/>
	</div>
</NetworkModal>

{#if openAlert}
	<ModalPrompt
		bind:showModal={openAlert}
		props={{
			callback: clearSSIDData,
			title: `${$format('forget_network_title', {
				default: 'Forget Network',
			})}?`,
			button_1: `${capFirstChar($format('title_forget', { default: 'forget' }))}`,
			button_1_variant: 'danger',
			button_2: $format('button_cancel'),
		}}
	>
		<div class="d-flex flex-direction-column align-items-center">
			<Icon class="mb-xs" icon="text-danger fa-light fa-wifi-slash" size="xxl" />
			<Text class="width-full">
				{$format('wifi_ssd_field_empy_msg', {
					default:
						'The Wi-Fi SSID field is empty. This will forget your saved network. Are you sure you want to proceed?',
				})}
			</Text>
		</div>
	</ModalPrompt>
{/if}
